/**
 * Asset Panel — slide-over detail panel for a single asset entry.
 *
 * Features: valuation cards with ROCAP merge display, inline editing,
 * select valuation, add practitioner valuation, document drill-in,
 * collapsible edit history.
 */

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useAction } from "convex/react";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { SlideOverPanel } from "../../shared-panels/SlideOverPanel";
import { LinkedRecordsSection } from "../../shared-panels/LinkedRecordsSection";
import { SuggestedLinksSection } from "../../shared-panels/SuggestedLinksSection";
import {
  ConfirmEditDialog,
  type PendingEdit,
} from "../../shared-panels/ConfirmEditDialog";
import type { RecordType } from "../../shared-panels/linkShared";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Textarea } from "@/ui/primitives/textarea";
import { ScrollArea } from "@/ui/primitives/scroll-area";
import { Separator } from "@/ui/primitives/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/ui/composed/sections/collapsible";
import {
  FileText,
  ExternalLink,
  Check,
  Plus,
  ChevronDown,
  ChevronUp,
  Pencil,
  X,
  MapPin,
  AlertTriangle,
  Info,
  EyeOff,
  Eye,
  Unlink,
  RefreshCw,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import { cn } from "@/utils/misc";
import {
  ChangeHistory,
  type ResolvedHistoryUser,
} from "../../shared-panels/ChangeHistory";
import {
  ASSET_FIELD_FORMATTERS,
  ASSET_FIELD_LABELS,
} from "./historyFormatters";
import { DocumentDetail } from "../../../documents/DocumentDetail";
import type { AssetEntryUI } from "../types";
import {
  formatFullCurrency,
  formatShortDate,
  formatSourceType,
  getSelectedValue,
  getRocapValue,
  getResolvedRocapValuation,
  isDerivedRocapValuation,
  getSelectedValuationObj,
  groupValuations,
  uniqueValuationSources,
  resolveRocapValuationIndex,
  valuationGroupSourceType,
  valuationGroupAsAtDate,
  SECURITY_LINK_WARNING,
  type AssetSecurityLinkStatus,
} from "../helpers";
import { NetValue, type SecuredCalcBreakdownUI } from "./NetValue";
import {
  ASSET_TYPE_LABEL,
  ASSET_TYPE_BADGE_COLOR,
  VALIDATION_FLAG_LABELS,
  getValuationReliability,
  getValuationSourceIcon,
} from "../constants";
import {
  FlagDismissButton,
  FlagDismissEditor,
  type UseFlagDismissalsResult,
} from "../../flags";
import {
  directorDisclosureLabel,
  type DirectorDisclosureLabel,
} from "@/lib/director-disclosure";

// ---------------------------------------------------------------------------
// Asset types for dropdown
// ---------------------------------------------------------------------------

const ASSET_TYPES = [
  "Cash at Bank",
  "Accounts Receivable",
  "Assets Subject to Lease",
  "Interests in Land",
  "Plant and Equipment",
  "Stock / Inventory",
  "Intangible Assets",
  "Contingent Assets",
] as const;

// ---------------------------------------------------------------------------
// Collapsible markdown notes
// ---------------------------------------------------------------------------

function normaliseNotesForMarkdown(raw: string): string {
  return raw.replace(/\s*•\s*/g, "\n- ").trim();
}

function CollapsibleNotes({ notes }: { notes: string }) {
  const [expanded, setExpanded] = useState(false);
  const normalisedNotes = notes ? normaliseNotesForMarkdown(notes) : "";

  if (!normalisedNotes)
    return <p className="text-sm text-muted-foreground">—</p>;

  return (
    <div>
      <div
        className={cn(
          "prose prose-sm relative max-w-none overflow-hidden transition-[max-height] duration-200 dark:prose-invert [&_li]:text-sm [&_li]:leading-relaxed [&_p]:text-sm [&_p]:leading-relaxed [&_p]:text-foreground [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-4",
          !expanded && "max-h-[180px]",
        )}
      >
        <ReactMarkdown>{normalisedNotes}</ReactMarkdown>
        {!expanded && (
          <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-background to-transparent" />
        )}
      </div>
      <button
        onClick={() => setExpanded(!expanded)}
        className="mt-2 rounded-md border border-primary/30 px-3 py-1 text-xs font-medium text-primary transition-colors hover:border-primary/50 hover:bg-primary/5"
      >
        {expanded ? "Show less" : "Show more"}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface AssetPanelProps {
  entry: AssetEntryUI | null;
  open: boolean;
  onClose: () => void;
  isViewer?: boolean;
  flagCtx: UseFlagDismissalsResult;
  placement?: "primary" | "secondary";
  /** Position in the secondary panel stack (1 = closest to primary). */
  stackIndex?: number;
  onOpenLinked?: (type: RecordType, id: string) => void;
  /** Creditor-traceable breakdown of this asset's secured deduction. */
  securedBreakdown?: SecuredCalcBreakdownUI;
  /** Why the secured net value can't be computed yet (undefined when fine). */
  securityLinkStatus?: AssetSecurityLinkStatus;
}

export function AssetPanel({
  entry,
  open,
  onClose,
  isViewer,
  flagCtx,
  placement = "primary",
  stackIndex,
  onOpenLinked,
  securedBreakdown,
  securityLinkStatus,
}: AssetPanelProps) {
  const [viewingDocumentId, setViewingDocumentId] =
    useState<Id<"documents"> | null>(null);

  return (
    <>
      <SlideOverPanel
        open={open}
        onClose={onClose}
        placement={placement}
        stackIndex={stackIndex}
      >
        {entry && (
          <AssetPanelContent
            entry={entry}
            isViewer={isViewer}
            flagCtx={flagCtx}
            onOpenLinked={onOpenLinked}
            securedBreakdown={securedBreakdown}
            securityLinkStatus={securityLinkStatus}
            onViewDocument={(docId) =>
              setViewingDocumentId(docId as Id<"documents">)
            }
          />
        )}
      </SlideOverPanel>

      <DocumentDetail
        documentId={viewingDocumentId}
        isViewer
        open={viewingDocumentId !== null}
        onClose={() => setViewingDocumentId(null)}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Inner content
// ---------------------------------------------------------------------------

function AssetPanelContent({
  entry,
  isViewer,
  flagCtx,
  onOpenLinked,
  securedBreakdown,
  securityLinkStatus,
  onViewDocument,
}: {
  entry: AssetEntryUI;
  isViewer?: boolean;
  flagCtx: UseFlagDismissalsResult;
  onOpenLinked?: (type: RecordType, id: string) => void;
  securedBreakdown?: SecuredCalcBreakdownUI;
  securityLinkStatus?: AssetSecurityLinkStatus;
  onViewDocument: (docId: string) => void;
}) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [showOtherValuations, setShowOtherValuations] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState("");
  // Edit awaiting practitioner confirmation (all inline edits pass through
  // the shared ConfirmEditDialog before any mutation fires).
  const [pendingEdit, setPendingEdit] = useState<PendingEdit | null>(null);

  const selectValuation = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.assetList.publicMutations.selectValuation,
    ),
  });

  const deselectValuation = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.assetList.publicMutations.deselectValuation,
    ),
  });

  const updateField = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.assetList.publicMutations.updateAssetField,
    ),
  });

  const addValuation = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.assetList.publicMutations
        .addPractitionerValuation,
    ),
  });

  const updateValuation = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.assetList.publicMutations
        .updateValuationAmount,
    ),
  });

  const toggleExcluded = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.assetList.publicMutations.toggleExcluded,
    ),
  });

  const overrideRecovery = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.assetList.publicMutations
        .overrideRecoveryEstimate,
    ),
  });

  const clearRecoveryOverride = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.assetList.publicMutations
        .clearRecoveryOverride,
    ),
  });

  const overrideRocap = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.assetList.publicMutations
        .overrideRocapValue,
    ),
  });

  const clearRocapOverride = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.assetList.publicMutations
        .clearRocapOverride,
    ),
  });

  // Estimated Recovery is manual-only: the practitioner runs the Asset Recovery
  // agent on demand (mirrors the "Run AI estimate" button on the list).
  const rerunAssetRecovery = useAction(
    api.jobs.summarization.actions.rerunAssetRecovery,
  );
  // Local bridge: true from click until the recovery task is observed running,
  // so the button can't be re-clicked in the schedule→start gap.
  const [runningEstimateBridge, setRunningEstimateBridge] = useState(false);
  const { data: recoveryTasks } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.getSummarizationTasksByJob,
      { jobId: entry.jobId },
    ),
  );
  // Director-disclosure label (ROCAP / SOA / …) for the job's appointment type.
  // Deduped by react-query against the list panel's own getJob.
  const { data: job } = useQuery(
    convexQuery(api.jobs.queries.getJob, { jobId: entry.jobId }),
  );
  const disclosure = directorDisclosureLabel(job?.appointmentType);
  const recoveryRunning = (recoveryTasks ?? []).some(
    (t) =>
      t.agentId === "sum_asset_recovery" &&
      (t.status === "running" || t.status === "waiting"),
  );
  useEffect(() => {
    if (recoveryRunning && runningEstimateBridge) {
      setRunningEstimateBridge(false);
    }
  }, [recoveryRunning, runningEstimateBridge]);
  const runningEstimate = runningEstimateBridge || recoveryRunning;

  async function handleRunEstimate() {
    if (runningEstimate) return;
    setRunningEstimateBridge(true);
    // Safety net: release the bridge if we never observe a running status
    // (e.g. the run no-ops instantly).
    const release = window.setTimeout(
      () => setRunningEstimateBridge(false),
      8000,
    );
    try {
      await rerunAssetRecovery({ jobId: entry.jobId });
    } catch {
      window.clearTimeout(release);
      setRunningEstimateBridge(false);
    }
  }

  const historyUserIds = useMemo<Array<Id<"users">>>(() => {
    if (!entry.changeHistory) return [];
    const ids = new Set<Id<"users">>();
    for (const change of entry.changeHistory) {
      if (change.userId) ids.add(change.userId);
    }
    return Array.from(ids);
  }, [entry.changeHistory]);

  const { data: historyUsers } = useQuery({
    ...convexQuery(api.users.queries.listUsersByIds, {
      ids: historyUserIds,
    }),
    enabled: historyUserIds.length > 0,
  });

  const historyUsersById = useMemo(() => {
    const map = new Map<Id<"users">, ResolvedHistoryUser>();
    for (const u of historyUsers ?? []) {
      map.set(u._id, u);
    }
    return map;
  }, [historyUsers]);

  const selVal = getSelectedValue(entry);
  const selObj = getSelectedValuationObj(entry);
  // Prioritise valuations visually by reliability of the record they come from
  // (most reliable first), tie-broken by the most recent as-at date.
  const valuationGroups = useMemo(() => {
    const groups = groupValuations(entry.valuations);
    return [...groups].sort((a, b) => {
      const rankA = getValuationReliability(valuationGroupSourceType(a)).rank;
      const rankB = getValuationReliability(valuationGroupSourceType(b)).rank;
      if (rankA !== rankB) return rankA - rankB;
      return valuationGroupAsAtDate(b).localeCompare(valuationGroupAsAtDate(a));
    });
  }, [entry.valuations]);

  // Resolve a source-document name to its documentId (when the asset carries
  // that document) so a valuation's provenance can open the source document.
  const docIdByName = useMemo(() => {
    const map = new Map<string, string>();
    for (const d of entry.sourceDocuments ?? []) {
      if (d.fileName) map.set(d.fileName, d.documentId);
    }
    return map;
  }, [entry.sourceDocuments]);

  const canEdit = !isViewer;

  // Once a valuation is accepted, the non-accepted ones are hidden behind a
  // single "Show N other valuations" toggle to keep the panel compact.
  const selectedValuationIndex = entry.selectedValuation?.valuationIndex ?? -1;
  const hasSelection = selectedValuationIndex >= 0;
  const isGroupSelected = (group: (typeof valuationGroups)[number]): boolean =>
    group.type === "single"
      ? group.originalIndex === selectedValuationIndex
      : group.originalIndices.includes(selectedValuationIndex);

  function renderValuationCard(
    group: (typeof valuationGroups)[number],
    gi: number,
  ) {
    const chosenRocapIdx = resolveRocapValuationIndex(
      entry.valuations,
      entry.chosenRocapValuationIndex,
    );
    if (group.type !== "single") {
      const isAnySelected = group.originalIndices.includes(
        selectedValuationIndex,
      );
      const isChosenRocap =
        chosenRocapIdx != null &&
        group.originalIndices.includes(chosenRocapIdx);
      const extraSources =
        group.type === "merged_sources"
          ? uniqueValuationSources(group.valuations)
          : undefined;
      return (
        <ValuationCard
          key={gi}
          valuation={group.primary}
          disclosure={disclosure}
          isSelected={isAnySelected}
          isChosenRocap={isChosenRocap}
          canEdit={canEdit}
          mergedDirectors={
            group.type === "merged_rocap" ? group.directors : undefined
          }
          extraSources={extraSources}
          onSelect={() =>
            isAnySelected
              ? deselectValuation.mutate({
                  entryId: entry._id as Id<"assetEntries">,
                })
              : selectValuation.mutate({
                  entryId: entry._id as Id<"assetEntries">,
                  valuationIndex: group.originalIndices[0],
                  reason: "Manually selected",
                })
          }
          onUpdateAmount={(newAmount) =>
            setPendingEdit({
              fieldLabel: "Valuation amount",
              onConfirm: (note) =>
                updateValuation.mutateAsync({
                  entryId: entry._id as Id<"assetEntries">,
                  valuationIndex: group.originalIndices[0],
                  newAmount,
                  note,
                }),
            })
          }
          securedBreakdown={securedBreakdown}
          onViewDocument={onViewDocument}
          docIdByName={docIdByName}
        />
      );
    }

    const v = group.valuation;
    const idx = group.originalIndex;
    const isSelected = selectedValuationIndex === idx;
    const isChosenRocap = chosenRocapIdx === idx;
    return (
      <ValuationCard
        key={gi}
        valuation={v}
        disclosure={disclosure}
        isSelected={isSelected}
        isChosenRocap={isChosenRocap}
        canEdit={canEdit}
        onSelect={() =>
          isSelected
            ? deselectValuation.mutate({
                entryId: entry._id as Id<"assetEntries">,
              })
            : selectValuation.mutate({
                entryId: entry._id as Id<"assetEntries">,
                valuationIndex: idx,
                reason: "Manually selected",
              })
        }
        onUpdateAmount={(newAmount) =>
          setPendingEdit({
            fieldLabel: "Valuation amount",
            onConfirm: (note) =>
              updateValuation.mutateAsync({
                entryId: entry._id as Id<"assetEntries">,
                valuationIndex: idx,
                newAmount,
                note,
              }),
          })
        }
        securedBreakdown={securedBreakdown}
        onViewDocument={onViewDocument}
        docIdByName={docIdByName}
      />
    );
  }

  function startEdit(field: string, currentValue: string) {
    if (!canEdit) return;
    setEditingField(field);
    setEditDraft(currentValue);
  }

  /**
   * Stages a field edit behind the shared confirmation dialog. The mutation
   * only fires once the practitioner confirms (optionally leaving a note).
   */
  function requestFieldEdit(field: string, value: unknown) {
    setPendingEdit({
      fieldLabel: ASSET_FIELD_LABELS[field] ?? field,
      onConfirm: (note) =>
        updateField.mutateAsync({
          entryId: entry._id as Id<"assetEntries">,
          field,
          value,
          note,
        }),
    });
  }

  function saveEdit(field: string) {
    requestFieldEdit(field, editDraft);
    setEditingField(null);
  }

  function cancelEdit() {
    setEditingField(null);
    setEditDraft("");
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="px-6 pb-4 pt-6">
        <div className="space-y-1">
          {editingField === "description" ? (
            <div className="flex items-center gap-2">
              <Input
                value={editDraft}
                onChange={(e) => setEditDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") saveEdit("description");
                  if (e.key === "Escape") cancelEdit();
                }}
                className="text-base font-semibold"
                autoFocus
              />
              <Button
                size="sm"
                variant="default"
                onClick={() => saveEdit("description")}
              >
                Save
              </Button>
              <Button size="sm" variant="ghost" onClick={cancelEdit}>
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          ) : (
            <h2
              className={cn(
                "line-clamp-2 text-lg font-semibold leading-tight",
                canEdit &&
                  "group cursor-pointer transition-colors hover:text-primary",
              )}
              onClick={() =>
                canEdit
                  ? startEdit("description", entry.description)
                  : undefined
              }
            >
              {entry.description.split(/\.\s/)[0]}
              {entry.description.includes(". ") ? "." : ""}
              {canEdit && (
                <Pencil className="ml-2 inline-block h-3 w-3 opacity-0 group-hover:opacity-50" />
              )}
            </h2>
          )}
        </div>
      </div>

      {/* Selected Value Banner */}
      <div
        className={cn(
          "border-y px-6 py-3",
          entry.isExcluded
            ? "border-border bg-muted/50"
            : "border-emerald-200/30 bg-emerald-50/50 dark:border-emerald-800/30 dark:bg-emerald-950/20",
        )}
      >
        <div className="flex items-start justify-between">
          <div>
            <p
              className={cn(
                "mb-1 text-xs font-semibold uppercase tracking-wider",
                entry.isExcluded
                  ? "text-muted-foreground"
                  : "text-emerald-700 dark:text-emerald-400",
              )}
            >
              Selected Value
            </p>
            <span
              className={cn(
                "block text-2xl font-bold tabular-nums",
                entry.isExcluded
                  ? "text-muted-foreground line-through"
                  : "text-foreground",
              )}
            >
              {selVal != null ? formatFullCurrency(selVal) : "Unselected"}
            </span>
            {selObj && !entry.isExcluded && (
              <p className="mt-0.5 text-xs text-emerald-700 dark:text-emerald-400">
                via {formatSourceType(selObj.sourceType)}
                {entry.selectedValuation?.selectedBy !== "system" &&
                  entry.selectedValuation?.selectedBy &&
                  ` · by ${entry.selectedValuation.selectedBy}`}
              </p>
            )}
            {entry.isExcluded && (
              <p className="mt-1 text-xs text-muted-foreground">
                This asset is excluded from the summary totals.
              </p>
            )}
            {!entry.isExcluded && (
              <NetValue
                size="lg"
                baseLabel="Selected value"
                baseValue={selVal}
                breakdown={securedBreakdown}
              />
            )}
            {!entry.isExcluded && securityLinkStatus && (
              <p className="mt-1.5 flex items-start gap-1.5 text-xs text-rose-600 dark:text-rose-400">
                <Unlink className="mt-px h-3.5 w-3.5 shrink-0" />
                <span>{SECURITY_LINK_WARNING[securityLinkStatus].full}</span>
              </p>
            )}
          </div>
          {canEdit && (
            <Button
              variant={entry.isExcluded ? "outline" : "ghost"}
              size="sm"
              className={cn(
                "shrink-0 gap-1.5",
                entry.isExcluded
                  ? "border-emerald-200 text-emerald-600 hover:bg-emerald-50 dark:border-emerald-800 dark:text-emerald-400 dark:hover:bg-emerald-950/30"
                  : "text-muted-foreground hover:text-foreground",
              )}
              onClick={() =>
                toggleExcluded.mutate({
                  entryId: entry._id as Id<"assetEntries">,
                  isExcluded: !entry.isExcluded,
                })
              }
            >
              {entry.isExcluded ? (
                <>
                  <Eye className="h-3.5 w-3.5" />
                  Include
                </>
              ) : (
                <>
                  <EyeOff className="h-3.5 w-3.5" />
                  Exclude
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      <Separator />

      {/* Scrollable body */}
      <ScrollArea className="min-h-0 flex-1 [&>[data-radix-scroll-area-viewport]>div]:!block">
        <div className="min-w-0 space-y-6 px-6 py-5">
          {/* Full description (when truncated in header) */}
          {entry.description.includes(". ") && (
            <PanelSection title="Description">
              {editingField === "description" ? (
                <div className="space-y-2">
                  <Textarea
                    value={editDraft}
                    onChange={(e) => setEditDraft(e.target.value)}
                    rows={4}
                    className="text-sm"
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => saveEdit("description")}>
                      Save
                    </Button>
                    <Button size="sm" variant="ghost" onClick={cancelEdit}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <p
                  className={cn(
                    "text-sm leading-relaxed text-foreground",
                    canEdit &&
                      "cursor-pointer transition-colors hover:text-primary/80",
                  )}
                  onClick={() =>
                    canEdit
                      ? startEdit("description", entry.description)
                      : undefined
                  }
                >
                  {entry.description}
                  {canEdit && (
                    <Pencil className="ml-2 inline-block h-3 w-3 opacity-0 hover:opacity-50" />
                  )}
                </p>
              )}
            </PanelSection>
          )}

          {/* Valuations — directly under selected value for clear connection */}
          <PanelSection
            title="Valuations"
            count={entry.valuations.length}
            action={
              canEdit && !showAddForm ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 gap-1 border-violet-200 text-xs text-violet-600 dark:border-violet-800 dark:text-violet-400"
                  onClick={() => setShowAddForm(true)}
                >
                  <Plus className="h-3 w-3" />
                  Add Valuation
                </Button>
              ) : undefined
            }
          >
            <div className="space-y-2">
              {(() => {
                const indexed = valuationGroups.map((group, gi) => ({
                  group,
                  gi,
                }));
                // Before anything is accepted, show every valuation in full.
                if (!hasSelection) {
                  return indexed.map(({ group, gi }) =>
                    renderValuationCard(group, gi),
                  );
                }
                const others = indexed.filter(
                  ({ group }) => !isGroupSelected(group),
                );
                return (
                  <>
                    {indexed
                      .filter(({ group }) => isGroupSelected(group))
                      .map(({ group, gi }) => renderValuationCard(group, gi))}

                    {others.length > 0 && (
                      <>
                        <button
                          type="button"
                          onClick={() => setShowOtherValuations((s) => !s)}
                          className="flex w-full items-center justify-center gap-1 rounded-lg border border-dashed border-border py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
                        >
                          {showOtherValuations ? (
                            <>
                              Hide other valuations
                              <ChevronUp className="h-3.5 w-3.5" />
                            </>
                          ) : (
                            <>
                              Show {others.length} other valuation
                              {others.length === 1 ? "" : "s"}
                              <ChevronDown className="h-3.5 w-3.5" />
                            </>
                          )}
                        </button>

                        {showOtherValuations &&
                          others.map(({ group, gi }) =>
                            renderValuationCard(group, gi),
                          )}
                      </>
                    )}
                  </>
                );
              })()}

              {entry.valuations.length === 0 && !showAddForm && (
                <p className="py-4 text-center text-sm text-muted-foreground">
                  No valuations yet.
                </p>
              )}

              {showAddForm && (
                <AddValuationForm
                  onAdd={(value, notes) => {
                    addValuation.mutate({
                      entryId: entry._id as Id<"assetEntries">,
                      value,
                      notes: notes || undefined,
                    });
                    setShowAddForm(false);
                  }}
                  onCancel={() => setShowAddForm(false)}
                />
              )}
            </div>
          </PanelSection>

          {/* ROCAP — single director figure (or practitioner override) */}
          <RocapSection
            entry={entry}
            disclosure={disclosure}
            canEdit={canEdit}
            onSave={(value, reason) =>
              overrideRocap.mutate({
                entryId: entry._id as Id<"assetEntries">,
                value,
                reason: reason || undefined,
              })
            }
            onClear={() =>
              clearRocapOverride.mutate({
                entryId: entry._id as Id<"assetEntries">,
              })
            }
          />

          {/* Practitioner range + independently-triggered AI estimate. */}
          <RecoverySection
            entry={entry}
            canEdit={canEdit}
            securedBreakdown={securedBreakdown}
            onViewDocument={onViewDocument}
            onRunEstimate={canEdit ? handleRunEstimate : undefined}
            runningEstimate={runningEstimate}
            onSave={(low, high, reason) =>
              overrideRecovery.mutate({
                entryId: entry._id as Id<"assetEntries">,
                low,
                high,
                reason: reason || undefined,
              })
            }
            onClear={() =>
              clearRecoveryOverride.mutate({
                entryId: entry._id as Id<"assetEntries">,
              })
            }
          />

          {/* Validation Flags — grouped by type, expandable */}
          {entry.validationFlags &&
            entry.validationFlags.length > 0 &&
            (() => {
              // Group flags by type
              const grouped = new Map<
                string,
                {
                  type: string;
                  reasons: string[];
                  severity: "warning" | "info";
                }
              >();
              for (const flag of entry.validationFlags) {
                const existing = grouped.get(flag.type);
                if (!existing) {
                  grouped.set(flag.type, {
                    type: flag.type,
                    reasons: [flag.reason],
                    severity: flag.severity,
                  });
                } else {
                  if (!existing.reasons.some((r) => r === flag.reason)) {
                    existing.reasons.push(flag.reason);
                  }
                  if (flag.severity === "warning")
                    existing.severity = "warning";
                }
              }
              const flagGroups = Array.from(grouped.values());

              return (
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wider text-[#121212]">
                    Flags ({flagGroups.length})
                  </p>
                  {flagGroups.map((group, i) => (
                    <Collapsible
                      key={i}
                      defaultOpen={group.reasons.length <= 2}
                    >
                      <div
                        className={cn(
                          "overflow-hidden rounded-lg border",
                          group.severity === "warning"
                            ? "border-rose-200 bg-rose-50/50 dark:border-rose-800 dark:bg-rose-950/20"
                            : "border-sky-200 bg-sky-50/50 dark:border-sky-800 dark:bg-sky-950/20",
                        )}
                      >
                        <CollapsibleTrigger className="group flex w-full items-center gap-3 px-3 py-2.5 text-left">
                          {group.severity === "warning" ? (
                            <AlertTriangle className="h-4 w-4 shrink-0 text-rose-500" />
                          ) : (
                            <Info className="h-4 w-4 shrink-0 text-sky-500" />
                          )}
                          <span
                            className={cn(
                              "flex-1 text-xs font-semibold uppercase tracking-wider",
                              group.severity === "warning"
                                ? "text-rose-700 dark:text-rose-400"
                                : "text-sky-700 dark:text-sky-400",
                            )}
                          >
                            {VALIDATION_FLAG_LABELS[group.type] ?? group.type}
                            {group.reasons.length > 1 && (
                              <span className="ml-1.5 text-[10px] font-normal opacity-70">
                                ({group.reasons.length})
                              </span>
                            )}
                          </span>
                          <ChevronDown
                            className={cn(
                              "h-3.5 w-3.5 shrink-0 transition-transform",
                              group.severity === "warning"
                                ? "text-rose-400 dark:text-rose-600"
                                : "text-sky-400 dark:text-sky-600",
                              "group-data-[state=open]:rotate-180",
                            )}
                          />
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div
                            className={cn(
                              "space-y-2 px-3 pb-2.5",
                              group.reasons.length > 1 && "pt-0",
                            )}
                          >
                            {group.reasons.map((reason, ri) => {
                              const dismissed = flagCtx.isDismissed(
                                "assetList",
                                entry._id,
                                { type: group.type, detail: reason },
                              );
                              return (
                                <div key={ri}>
                                  <div
                                    className={cn(
                                      "flex items-start gap-1.5 pl-7 text-sm text-muted-foreground",
                                      group.reasons.length > 1 &&
                                        "ml-[5px] border-l-2 pl-4",
                                      group.severity === "warning"
                                        ? "border-rose-200 dark:border-rose-800"
                                        : "border-sky-200 dark:border-sky-800",
                                    )}
                                  >
                                    <span
                                      className={cn(
                                        "flex-1",
                                        dismissed && "line-through opacity-40",
                                      )}
                                    >
                                      {reason}
                                    </span>
                                    <FlagDismissButton
                                      ctx={flagCtx}
                                      agentKind="assetList"
                                      entryId={entry._id}
                                      flagType={group.type}
                                      flagDetail={reason}
                                      className="mt-0.5 shrink-0"
                                    />
                                  </div>
                                  <FlagDismissEditor
                                    ctx={flagCtx}
                                    agentKind="assetList"
                                    entryId={entry._id}
                                    entryLabel={entry.description}
                                    flagType={group.type}
                                    flagLabel={
                                      VALIDATION_FLAG_LABELS[group.type] ??
                                      group.type
                                    }
                                    flagDetail={reason}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        </CollapsibleContent>
                      </div>
                    </Collapsible>
                  ))}
                </div>
              );
            })()}

          {/* Type */}
          <PanelSection title="Type">
            {canEdit ? (
              <Select
                value={entry.assetType}
                onValueChange={(val) => requestFieldEdit("assetType", val)}
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ASSET_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Badge
                color={ASSET_TYPE_BADGE_COLOR[entry.assetType]}
                variant="soft"
                size="sm"
              >
                {ASSET_TYPE_LABEL[entry.assetType] ?? entry.assetType}
              </Badge>
            )}
          </PanelSection>

          {/* Status */}
          <PanelSection title="Status">
            {canEdit ? (
              <Select
                value={entry.assetStatus}
                onValueChange={(val) => requestFieldEdit("assetStatus", val)}
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Identified">Identified</SelectItem>
                  <SelectItem value="Missing">Missing</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <span
                className={cn(
                  "text-sm font-medium",
                  entry.assetStatus === "Identified"
                    ? "text-emerald-600"
                    : "text-rose-600",
                )}
              >
                {entry.assetStatus}
              </span>
            )}
          </PanelSection>

          {/* Security Interest */}
          <PanelSection title="Subject to Security Interest">
            {canEdit ? (
              <Select
                value={entry.subjectToSecurityInterest ? "yes" : "no"}
                onValueChange={(val) =>
                  requestFieldEdit("subjectToSecurityInterest", val === "yes")
                }
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <span
                className={cn(
                  "text-sm font-medium",
                  entry.subjectToSecurityInterest
                    ? "text-amber-600"
                    : "text-muted-foreground",
                )}
              >
                {entry.subjectToSecurityInterest ? "Yes" : "No"}
              </span>
            )}
          </PanelSection>

          {/* Location */}
          <PanelSection title="Location">
            {editingField === "location" ? (
              <div className="flex items-center gap-2">
                <Input
                  value={editDraft}
                  onChange={(e) => setEditDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveEdit("location");
                    if (e.key === "Escape") cancelEdit();
                  }}
                  className="h-9 text-sm"
                  autoFocus
                />
                <Button size="sm" onClick={() => saveEdit("location")}>
                  Save
                </Button>
                <Button size="sm" variant="ghost" onClick={cancelEdit}>
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            ) : (
              <p
                className={cn(
                  "flex items-center gap-2 text-sm text-foreground",
                  canEdit && "group cursor-pointer hover:text-primary",
                )}
                onClick={() => startEdit("location", entry.location)}
              >
                <MapPin className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                {entry.location || "—"}
                {canEdit && (
                  <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-50" />
                )}
              </p>
            )}
          </PanelSection>

          {/* Notes */}
          <PanelSection
            title="Notes"
            action={
              canEdit && editingField !== "notes" ? (
                <button
                  onClick={() => startEdit("notes", entry.notes)}
                  className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  <Pencil className="h-3 w-3" />
                </button>
              ) : undefined
            }
          >
            {editingField === "notes" ? (
              <div className="space-y-2">
                <Textarea
                  value={editDraft}
                  onChange={(e) => setEditDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") cancelEdit();
                  }}
                  rows={5}
                  className="text-sm"
                  autoFocus
                />
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => saveEdit("notes")}>
                    Save
                  </Button>
                  <Button size="sm" variant="ghost" onClick={cancelEdit}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <CollapsibleNotes notes={entry.notes} />
            )}
          </PanelSection>

          {/* Source Documents */}
          {entry.sourceDocuments.length > 0 && (
            <PanelSection
              title="Source Documents"
              count={entry.sourceDocuments.length}
              collapsible
              defaultOpen={false}
            >
              <div className="space-y-1.5">
                {entry.sourceDocuments.map((doc) => (
                  <button
                    key={doc.documentId}
                    type="button"
                    onClick={() => onViewDocument(doc.documentId)}
                    className="group flex w-full items-center gap-3 rounded-lg border border-border bg-background px-3 py-2.5 text-left transition-colors hover:bg-muted/50"
                  >
                    <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span className="flex-1 truncate text-sm text-foreground">
                      {doc.fileName}
                    </span>
                    <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground/0 transition-colors group-hover:text-muted-foreground" />
                  </button>
                ))}
              </div>
            </PanelSection>
          )}

          {/* Suggested links */}
          {onOpenLinked && (
            <SuggestedLinksSection
              jobId={entry.jobId}
              anchor={{ type: "asset", id: entry._id }}
              onOpenLinked={onOpenLinked}
              isViewer={isViewer}
            />
          )}

          {/* Linked records */}
          {onOpenLinked && (
            <LinkedRecordsSection
              jobId={entry.jobId}
              anchor={{ type: "asset", id: entry._id }}
              onOpenLinked={onOpenLinked}
              isViewer={isViewer}
            />
          )}

          {/* History (collapsible) */}
          <ChangeHistory
            history={entry.changeHistory}
            usersById={historyUsersById}
            formatters={ASSET_FIELD_FORMATTERS}
            fieldLabels={ASSET_FIELD_LABELS}
            persistId="asset-detail:history"
          />
        </div>
      </ScrollArea>

      {/* Confirmation gate for every inline edit in this panel */}
      <ConfirmEditDialog
        edit={pendingEdit}
        onClose={() => setPendingEdit(null)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Estimated Recovery Section
// ---------------------------------------------------------------------------

function formatRecoveryTimestamp(ts: number | undefined): string | null {
  if (ts == null) return null;
  try {
    return new Date(ts).toLocaleString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return null;
  }
}

function RocapSection({
  entry,
  disclosure,
  canEdit,
  onSave,
  onClear,
}: {
  entry: AssetEntryUI;
  /** Director-disclosure label for the appointment type (ROCAP / SOA / …). */
  disclosure: DirectorDisclosureLabel;
  canEdit: boolean;
  /** When provided (and canEdit), enables the practitioner override controls. */
  onSave?: (value: number, reason: string) => void;
  onClear?: () => void;
}) {
  const rocapValue = getRocapValue(entry);
  const isOverride = entry.rocapOverride != null;
  const hasValue = rocapValue != null;
  // Prefer the persisted recovery-agent index; otherwise infer from an
  // explicit ROCAP tag or a Form 507 / annexure source (e.g. Part A4).
  // When the source isn't the ROCAP form itself, label it "Derived from ROCAP".
  const chosenValuation = getResolvedRocapValuation(entry);
  const isDerivedRocap = isDerivedRocapValuation(chosenValuation);
  const editable = canEdit && !!onSave;

  const [editing, setEditing] = useState(false);
  const [valueDraft, setValueDraft] = useState("");
  const [reasonDraft, setReasonDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [derivedOpen, setDerivedOpen] = useState(false);

  function beginEdit() {
    setValueDraft(rocapValue != null ? String(rocapValue) : "");
    setReasonDraft("");
    setError(null);
    setEditing(true);
  }

  function commit() {
    const num = parseFloat(valueDraft);
    if (isNaN(num)) {
      setError(`Enter a ${disclosure.short} figure.`);
      return;
    }
    if (num < 0) {
      setError("Figure cannot be negative.");
      return;
    }
    onSave?.(num, reasonDraft.trim());
    setEditing(false);
  }

  return (
    <PanelSection
      title={disclosure.short}
      action={
        editable && !editing ? (
          <Button
            variant="outline"
            size="sm"
            className="h-7 gap-1 text-xs"
            onClick={beginEdit}
          >
            <Pencil className="h-3 w-3" />
            {hasValue ? "Override" : `Set ${disclosure.short}`}
          </Button>
        ) : undefined
      }
    >
      {editing ? (
        <div className="space-y-3 rounded-lg border-2 border-dashed border-amber-200 p-4 dark:border-amber-800">
          <p className="text-sm font-semibold text-amber-700 dark:text-amber-400">
            {hasValue
              ? `Override ${disclosure.short}`
              : `Set ${disclosure.short} value`}
          </p>
          <p className="text-xs text-muted-foreground">
            Manually setting this locks it — the Asset Recovery agent won't
            change the {disclosure.short} figure on its next run.
          </p>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {disclosure.short} ($)
            </label>
            <Input
              type="number"
              value={valueDraft}
              onChange={(e) => setValueDraft(e.target.value)}
              placeholder="0.00"
              className="h-9 text-sm tabular-nums"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Reason (optional)
            </label>
            <Textarea
              value={reasonDraft}
              onChange={(e) => setReasonDraft(e.target.value)}
              placeholder={`Why are you setting this ${disclosure.short} figure?`}
              rows={2}
              className="text-sm"
            />
          </div>
          {error && <p className="text-xs text-rose-600">{error}</p>}
          <div className="flex gap-2">
            <Button className="flex-1" onClick={commit}>
              Save &amp; Lock
            </Button>
            <Button variant="outline" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {hasValue && (
            <div className="rounded-lg border border-border bg-background p-3">
              <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {disclosure.short} figure
              </p>
              <p className="text-lg font-bold tabular-nums text-foreground">
                {formatFullCurrency(rocapValue)}
              </p>
              {isDerivedRocap && chosenValuation && (
                <Collapsible
                  open={derivedOpen}
                  onOpenChange={setDerivedOpen}
                  className="mt-1.5"
                >
                  <CollapsibleTrigger className="flex items-center gap-1 text-[10px] font-medium text-muted-foreground hover:text-foreground">
                    {derivedOpen ? "Hide source" : "Show source"}
                    <ChevronDown
                      className={cn(
                        "h-3 w-3 transition-transform",
                        derivedOpen && "rotate-180",
                      )}
                    />
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <p className="mt-1.5 text-xs text-muted-foreground">
                      Not explicitly set out in the {disclosure.full} — derived
                      from a{" "}
                      {formatSourceType(
                        chosenValuation.sourceType,
                      ).toLowerCase()}
                      {chosenValuation.sourceDocument
                        ? ` annexed to the ${disclosure.full} (${chosenValuation.sourceDocument.name})`
                        : ` disclosed via the ${disclosure.full}`}
                      .
                    </p>
                  </CollapsibleContent>
                </Collapsible>
              )}
            </div>
          )}
          {(isOverride || !hasValue) && (
            <div className="flex flex-wrap items-center gap-2">
              {isOverride ? (
                <Badge color="amber" variant="soft" size="sm">
                  Practitioner override (locked)
                </Badge>
              ) : (
                <Badge color="red" variant="soft" size="sm">
                  No {disclosure.short} figure
                </Badge>
              )}
              {editable && isOverride && onClear && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-auto h-6 text-xs text-muted-foreground hover:text-foreground"
                  onClick={onClear}
                >
                  Reset to agent {disclosure.short}
                </Button>
              )}
            </div>
          )}
          {!hasValue && (
            <p className="text-sm text-muted-foreground">
              No director {disclosure.short} figure for this asset. Use “Set{" "}
              {disclosure.short}” to enter one.
            </p>
          )}
        </div>
      )}
    </PanelSection>
  );
}

/**
 * Renders the citation list for a recovery estimate (web links, job documents,
 * and plain valuation references). Tiny stateless leaf shared by the
 * practitioner-range and AI-estimate detail blocks.
 */
function RecoverySourceList({
  sources,
  onViewDocument,
}: {
  sources: NonNullable<
    NonNullable<AssetEntryUI["estimatedRecovery"]>["sources"]
  >;
  onViewDocument: (docId: string) => void;
}) {
  return (
    <div className="space-y-1.5">
      <p className="text-xs font-semibold uppercase tracking-wider text-[#717112]">
        Sources ({sources.length})
      </p>
      {sources.map((src, si) => {
        if (src.kind === "web" && src.url) {
          return (
            <a
              key={si}
              href={src.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex w-full items-center gap-3 rounded-lg border border-border bg-background px-3 py-2 text-left transition-colors hover:bg-muted/50"
            >
              <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="flex-1 truncate text-sm text-foreground">
                {src.label}
              </span>
              <span className="shrink-0 text-[10px] text-muted-foreground">
                Web
              </span>
            </a>
          );
        }
        if (src.kind === "document" && src.documentId) {
          const docId = src.documentId;
          return (
            <button
              key={si}
              type="button"
              onClick={() => onViewDocument(docId)}
              className="group flex w-full items-center gap-3 rounded-lg border border-border bg-background px-3 py-2 text-left transition-colors hover:bg-muted/50"
            >
              <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="flex-1 truncate text-sm text-foreground">
                {src.label}
              </span>
              <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground/0 transition-colors group-hover:text-muted-foreground" />
            </button>
          );
        }
        return (
          <div
            key={si}
            className="flex w-full items-center gap-3 rounded-lg border border-border bg-muted/20 px-3 py-2 text-left"
          >
            <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span className="flex-1 truncate text-sm text-muted-foreground">
              {src.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** Markdown wrapper for a recovery rationale. Tiny stateless leaf. */
function RecoveryRationale({ markdown }: { markdown: string }) {
  return (
    <div className="prose prose-sm max-w-none text-muted-foreground dark:prose-invert [&_li]:text-sm [&_li]:leading-relaxed [&_li]:text-muted-foreground [&_p:last-child]:mb-0 [&_p]:text-sm [&_p]:leading-relaxed [&_p]:text-muted-foreground [&_strong]:text-foreground [&_ul]:my-0 [&_ul]:list-disc [&_ul]:pl-4">
      <ReactMarkdown>{markdown}</ReactMarkdown>
    </div>
  );
}

function RecoverySection({
  entry,
  canEdit,
  onViewDocument,
  onSave,
  onClear,
  onRunEstimate,
  runningEstimate,
  securedBreakdown,
}: {
  entry: AssetEntryUI;
  canEdit: boolean;
  onViewDocument: (docId: string) => void;
  /** When provided (and canEdit), enables the practitioner estimate controls. */
  onSave?: (low: number, high: number, reason: string) => void;
  onClear?: () => void;
  /** When provided, triggers a manual Asset Recovery agent run for the job. */
  onRunEstimate?: () => void;
  /** True while an Asset Recovery run is scheduled or in flight. */
  runningEstimate?: boolean;
  /** Creditor-traceable secured breakdown (for net-of-security figures). */
  securedBreakdown?: SecuredCalcBreakdownUI;
}) {
  const recovery = entry.estimatedRecovery;
  const low = recovery?.low;
  const high = recovery?.high;
  const practitionerSet = recovery?.isOverride === true;
  const hasRange = low != null || high != null;
  const rangeInsufficientInfo =
    !practitionerSet && recovery?.insufficientInformation === true;

  const ai = recovery?.aiEstimate;
  const aiInsufficientInfo = ai?.insufficientInformation === true;

  const [editing, setEditing] = useState(false);
  const [lowDraft, setLowDraft] = useState(low != null ? String(low) : "");
  const [highDraft, setHighDraft] = useState(high != null ? String(high) : "");
  const [reasonDraft, setReasonDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [rangeDetailsOpen, setRangeDetailsOpen] = useState(false);
  const [aiDetailsOpen, setAiDetailsOpen] = useState(false);

  const editable = canEdit && !!onSave;
  const rangeUpdatedLabel = formatRecoveryTimestamp(recovery?.updatedAt);
  const aiUpdatedLabel = formatRecoveryTimestamp(ai?.updatedAt);

  function beginEdit() {
    setLowDraft(low != null ? String(low) : "");
    setHighDraft(high != null ? String(high) : "");
    setReasonDraft("");
    setError(null);
    setEditing(true);
  }

  function commit() {
    const lowNum = parseFloat(lowDraft);
    const highNum = parseFloat(highDraft);
    if (isNaN(lowNum) || isNaN(highNum)) {
      setError("Enter both a low and a high figure.");
      return;
    }
    if (lowNum < 0 || highNum < 0) {
      setError("Figures cannot be negative.");
      return;
    }
    if (lowNum > highNum) {
      setError("Low cannot exceed high.");
      return;
    }
    onSave?.(lowNum, highNum, reasonDraft.trim());
    setEditing(false);
  }

  return (
    <>
      <PanelSection
        title="Practitioner Estimates"
        action={
          editable && !editing ? (
            <Button
              variant="outline"
              size="sm"
              className="h-7 gap-1 text-xs"
              onClick={beginEdit}
            >
              <Pencil className="h-3 w-3" />
              {hasRange ? "Edit estimate" : "Set estimate"}
            </Button>
          ) : undefined
        }
      >
        {editing ? (
          <div className="space-y-3 rounded-lg border-2 border-dashed border-border p-4">
            <p className="text-sm font-semibold text-foreground">
              Set estimated recovery
            </p>
            <p className="text-xs text-muted-foreground">
              Your low and high figures for what this asset will realise. The AI
              estimate is separate and never changes them.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Low ($)
                </label>
                <Input
                  type="number"
                  value={lowDraft}
                  onChange={(e) => setLowDraft(e.target.value)}
                  placeholder="0.00"
                  className="h-9 text-sm tabular-nums"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  High ($)
                </label>
                <Input
                  type="number"
                  value={highDraft}
                  onChange={(e) => setHighDraft(e.target.value)}
                  placeholder="0.00"
                  className="h-9 text-sm tabular-nums"
                />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Reason (optional)
              </label>
              <Textarea
                value={reasonDraft}
                onChange={(e) => setReasonDraft(e.target.value)}
                placeholder="Basis for these figures?"
                rows={2}
                className="text-sm"
              />
            </div>
            {error && <p className="text-xs text-rose-600">{error}</p>}
            <div className="flex gap-2">
              <Button className="flex-1" onClick={commit}>
                Save
              </Button>
              <Button variant="outline" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : !hasRange ? (
          <p className="text-sm text-muted-foreground">
            No practitioner estimate yet. Enter your own low and high recovery
            figures for this asset.
          </p>
        ) : (
          <div className="space-y-3">
            {/* Low / High figures */}
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-border bg-background p-3">
                <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Low
                </p>
                <p className="text-lg font-bold tabular-nums text-foreground">
                  {formatFullCurrency(low ?? null)}
                </p>
                <NetValue
                  baseLabel="Low estimate"
                  baseValue={low}
                  breakdown={securedBreakdown}
                />
              </div>
              <div className="rounded-lg border border-border bg-background p-3">
                <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  High
                </p>
                <p className="text-lg font-bold tabular-nums text-foreground">
                  {formatFullCurrency(high ?? null)}
                </p>
                <NetValue
                  baseLabel="High estimate"
                  baseValue={high}
                  breakdown={securedBreakdown}
                />
              </div>
            </div>

            {/* Provenance + rationale + sources */}
            <Collapsible
              open={rangeDetailsOpen}
              onOpenChange={setRangeDetailsOpen}
              className="rounded-lg border border-border bg-background"
            >
              <CollapsibleTrigger className="group flex w-full items-center gap-2 px-3 py-2 text-left">
                {practitionerSet ? (
                  <Badge color="blue" variant="soft" size="sm">
                    Practitioner set
                  </Badge>
                ) : rangeInsufficientInfo ? (
                  <Badge color="amber" variant="soft" size="sm">
                    Insufficient information
                  </Badge>
                ) : (
                  <Badge color="gray" variant="soft" size="sm">
                    Agent estimate (legacy)
                  </Badge>
                )}
                {rangeUpdatedLabel && (
                  <span className="text-[10px] text-muted-foreground">
                    Updated {rangeUpdatedLabel}
                  </span>
                )}
                <span className="ml-auto flex items-center gap-1 text-[10px] font-medium text-muted-foreground">
                  {rangeDetailsOpen ? "Hide details" : "Show details"}
                  <ChevronDown
                    className={cn(
                      "h-3.5 w-3.5 transition-transform",
                      rangeDetailsOpen && "rotate-180",
                    )}
                  />
                </span>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-3 px-3 pb-3">
                {editable && practitionerSet && onClear && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs text-muted-foreground hover:text-foreground"
                    onClick={onClear}
                  >
                    Clear practitioner marker
                  </Button>
                )}
                {recovery?.rationale && (
                  <RecoveryRationale markdown={recovery.rationale} />
                )}
                {recovery?.sources && recovery.sources.length > 0 && (
                  <RecoverySourceList
                    sources={recovery.sources}
                    onViewDocument={onViewDocument}
                  />
                )}
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}
      </PanelSection>

      <PanelSection
        title="AI Estimate"
        action={
          onRunEstimate ? (
            <Button
              variant="outline"
              size="sm"
              className="h-7 gap-1.5 text-xs"
              disabled={runningEstimate}
              onClick={onRunEstimate}
            >
              <RefreshCw
                className={cn("h-3.5 w-3.5", runningEstimate && "animate-spin")}
              />
              {runningEstimate ? "Running…" : ai ? "Re-run" : "Run AI estimate"}
            </Button>
          ) : undefined
        }
      >
        {!ai ? (
          <p className="text-sm text-muted-foreground">
            No AI estimate yet. Run the AI to get an independent single estimate
            of realisable value for every asset on this job.
          </p>
        ) : (
          <div className="space-y-3">
            {aiInsufficientInfo && (
              <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950/40">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                <p className="text-xs text-amber-800 dark:text-amber-300">
                  Not enough information for the AI to provide an accurate
                  estimate — it defaulted to $0. Add a valuation or run the AI
                  estimate again once more detail is available.
                </p>
              </div>
            )}

            <div className="rounded-lg border border-border bg-background p-3">
              <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Estimated value
              </p>
              <p className="text-lg font-bold tabular-nums text-foreground">
                {formatFullCurrency(ai.value)}
              </p>
              <NetValue
                baseLabel="AI estimate"
                baseValue={ai.value}
                breakdown={securedBreakdown}
              />
            </div>

            <Collapsible
              open={aiDetailsOpen}
              onOpenChange={setAiDetailsOpen}
              className="rounded-lg border border-border bg-background"
            >
              <CollapsibleTrigger className="group flex w-full items-center gap-2 px-3 py-2 text-left">
                {aiInsufficientInfo ? (
                  <Badge color="amber" variant="soft" size="sm">
                    Insufficient information
                  </Badge>
                ) : (
                  <Badge color="gray" variant="soft" size="sm">
                    AI estimate
                  </Badge>
                )}
                {aiUpdatedLabel && (
                  <span className="text-[10px] text-muted-foreground">
                    Updated {aiUpdatedLabel}
                  </span>
                )}
                <span className="ml-auto flex items-center gap-1 text-[10px] font-medium text-muted-foreground">
                  {aiDetailsOpen ? "Hide details" : "Show details"}
                  <ChevronDown
                    className={cn(
                      "h-3.5 w-3.5 transition-transform",
                      aiDetailsOpen && "rotate-180",
                    )}
                  />
                </span>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-3 px-3 pb-3">
                {ai.rationale && <RecoveryRationale markdown={ai.rationale} />}
                {ai.sources && ai.sources.length > 0 && (
                  <RecoverySourceList
                    sources={ai.sources}
                    onViewDocument={onViewDocument}
                  />
                )}
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}
      </PanelSection>
    </>
  );
}

// ---------------------------------------------------------------------------
// Valuation Card
// ---------------------------------------------------------------------------

type ValuationSourceDoc = NonNullable<
  AssetEntryUI["valuations"][number]["sourceDocument"]
> & { asAtDate?: string };

/** Provenance rows for a valuation card. Tiny stateless leaf. */
function ValuationSourceRows({
  sources,
  isOverride,
  showDates,
  onViewDocument,
  docIdByName,
}: {
  sources: ValuationSourceDoc[];
  isOverride: boolean;
  /** Show each source's own as-at date (used when a card merges several
   *  documents that report the figure at different dates). */
  showDates: boolean;
  onViewDocument?: (docId: string) => void;
  docIdByName?: Map<string, string>;
}) {
  if (sources.length === 0) {
    return (
      <div className="mt-2 flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <FileText className="h-3 w-3 shrink-0 opacity-50" />
        <span>
          <span className="font-medium text-foreground">Source: </span>
          {isOverride
            ? "Practitioner entry (no document)"
            : "Not linked to a document"}
        </span>
      </div>
    );
  }

  return (
    <div className="mt-2 space-y-1">
      {sources.map((doc, si) => {
        const docId = docIdByName?.get(doc.name);
        const label = doc.section ? `${doc.name} · ${doc.section}` : doc.name;
        const prefix = sources.length === 1 ? "Source:" : `Source ${si + 1}:`;
        const dateLabel =
          showDates && doc.asAtDate ? formatShortDate(doc.asAtDate) : null;
        if (docId && onViewDocument) {
          return (
            <button
              key={`${doc.name}-${si}`}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onViewDocument(docId);
              }}
              className="group/src flex w-full items-center gap-1.5 rounded-md border border-border bg-muted/30 px-2 py-1.5 text-left transition-colors hover:bg-muted/60"
            >
              <FileText className="h-3 w-3 shrink-0 text-muted-foreground" />
              <span className="shrink-0 text-[11px] font-medium text-foreground">
                {prefix}
              </span>
              <span className="min-w-0 flex-1 truncate text-[11px] text-muted-foreground">
                {label}
              </span>
              {dateLabel && (
                <span className="shrink-0 whitespace-nowrap text-[10px] tabular-nums text-muted-foreground">
                  {dateLabel}
                </span>
              )}
              <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground/0 transition-colors group-hover/src:text-muted-foreground" />
            </button>
          );
        }
        return (
          <div
            key={`${doc.name}-${si}`}
            className="flex items-center gap-1.5 rounded-md border border-border bg-muted/30 px-2 py-1.5"
          >
            <FileText className="h-3 w-3 shrink-0 text-muted-foreground" />
            <span className="shrink-0 text-[11px] font-medium text-foreground">
              {prefix}
            </span>
            <span className="min-w-0 flex-1 truncate text-[11px] text-muted-foreground">
              {label}
            </span>
            {dateLabel && (
              <span className="shrink-0 whitespace-nowrap text-[10px] tabular-nums text-muted-foreground">
                {dateLabel}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ValuationCard({
  valuation,
  disclosure,
  isSelected,
  isChosenRocap,
  canEdit,
  mergedDirectors,
  onSelect,
  onUpdateAmount,
  securedBreakdown,
  onViewDocument,
  docIdByName,
  extraSources,
}: {
  valuation: AssetEntryUI["valuations"][number];
  /** Director-disclosure label for the appointment type (ROCAP / SOA / …). */
  disclosure: DirectorDisclosureLabel;
  isSelected: boolean;
  isChosenRocap?: boolean;
  canEdit: boolean;
  mergedDirectors?: string[];
  onSelect: () => void;
  onUpdateAmount: (amount: number) => void;
  /** Creditor-traceable secured breakdown (for net-of-security figure). */
  securedBreakdown?: SecuredCalcBreakdownUI;
  /** Opens a source document by id (for provenance drill-in). */
  onViewDocument?: (docId: string) => void;
  /** Resolves a source-document name to its documentId, when available. */
  docIdByName?: Map<string, string>;
  /**
   * Extra source documents (each with its own as-at date) when several
   * extracts of the same figure have been collapsed into this card. When set,
   * these replace the single `valuation.sourceDocument` line.
   */
  extraSources?: ValuationSourceDoc[];
}) {
  const [editingAmount, setEditingAmount] = useState(false);
  const [amountDraft, setAmountDraft] = useState(String(valuation.value));

  const icon = getValuationSourceIcon(valuation.sourceType);
  const isOverride = valuation.sourceType === "Practitioner Override";
  const reliability = getValuationReliability(valuation.sourceType);
  // A raw "ROCAP" source is relabelled to the appointment's disclosure term.
  const rawSourceLabel = formatSourceType(valuation.sourceType);
  const sourceLabel =
    rawSourceLabel === "ROCAP" ? disclosure.short : rawSourceLabel;
  const sources: ValuationSourceDoc[] =
    extraSources && extraSources.length > 0
      ? extraSources
      : valuation.sourceDocument
        ? [{ ...valuation.sourceDocument, asAtDate: valuation.asAtDate }]
        : [];
  // A merged card spans several documents that may report the figure at
  // different dates, so we drop the single header date and instead show each
  // source's own date on its provenance row.
  const isMergedSources = sources.length > 1;
  const distinctSourceDates = new Set(
    sources.map((s) => s.asAtDate ?? "").filter((d) => d !== ""),
  );
  const showSourceDates = isMergedSources && distinctSourceDates.size > 1;

  function saveAmount() {
    const parsed = parseFloat(amountDraft);
    if (!isNaN(parsed) && parsed !== valuation.value) {
      onUpdateAmount(parsed);
    }
    setEditingAmount(false);
  }

  return (
    <div
      onClick={canEdit ? onSelect : undefined}
      className={cn(
        "rounded-lg border p-3 transition-colors",
        isSelected
          ? "border-emerald-500 bg-emerald-50/30 dark:bg-emerald-950/20"
          : "border-border hover:border-muted-foreground/30",
        canEdit && "cursor-pointer",
      )}
    >
      {/* Top row: source type + check */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <div
            className={cn(
              "flex h-6 w-6 shrink-0 items-center justify-center rounded text-[10px] font-bold",
              reliability.iconClass,
            )}
          >
            {icon}
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-1">
              <span className="text-xs font-semibold text-foreground">
                {sourceLabel}
              </span>
              {!mergedDirectors && valuation.directorName && (
                <span className="text-xs text-muted-foreground">
                  — {valuation.directorName}
                </span>
              )}
              {mergedDirectors && (
                <span className="text-xs text-muted-foreground">
                  — {mergedDirectors.join(", ")}
                </span>
              )}
            </div>
            {/* Reliability tier — how much weight to place on this record */}
            <div className="mt-0.5 flex items-center gap-1">
              <span
                className={cn(
                  "h-1.5 w-1.5 shrink-0 rounded-full",
                  reliability.dotClass,
                )}
              />
              <span
                className={cn("text-[10px] font-medium", reliability.textClass)}
              >
                {reliability.label}
              </span>
            </div>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {!showSourceDates && (
            <span className="whitespace-nowrap text-[10px] text-muted-foreground">
              {formatShortDate(valuation.asAtDate)}
            </span>
          )}
          {isChosenRocap && (
            <Badge
              color="indigo"
              variant="soft"
              size="sm"
              className="whitespace-nowrap"
            >
              {disclosure.short} (chosen)
            </Badge>
          )}
          {isSelected && (
            <div className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500">
              <Check className="h-3 w-3 text-white" />
            </div>
          )}
        </div>
      </div>

      {/* Bottom row: value */}
      <div className="mt-2 flex items-center justify-between gap-2">
        <div className="min-w-0">
          {editingAmount && canEdit ? (
            <div
              className="flex items-center gap-2"
              onClick={(e) => e.stopPropagation()}
            >
              <Input
                type="number"
                value={amountDraft}
                onChange={(e) => setAmountDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") saveAmount();
                  if (e.key === "Escape") {
                    setAmountDraft(String(valuation.value));
                    setEditingAmount(false);
                  }
                }}
                className="h-7 w-32 text-sm font-semibold tabular-nums"
                autoFocus
              />
              <Button
                size="sm"
                className="h-7 text-xs"
                onClick={(e) => {
                  e.stopPropagation();
                  saveAmount();
                }}
              >
                Save
              </Button>
            </div>
          ) : (
            <span
              className={cn(
                "text-sm font-bold tabular-nums",
                isSelected
                  ? "text-emerald-700 dark:text-emerald-400"
                  : "text-foreground",
                canEdit && "cursor-text",
              )}
              onClick={(e) => {
                if (!canEdit) return;
                e.stopPropagation();
                setAmountDraft(String(valuation.value));
                setEditingAmount(true);
              }}
            >
              {formatFullCurrency(valuation.value)}
              {canEdit && (
                <Pencil className="ml-1 inline-block h-2.5 w-2.5 opacity-30" />
              )}
            </span>
          )}
        </div>
        <div className="-mt-1 shrink-0">
          <NetValue
            baseLabel={formatSourceType(valuation.sourceType)}
            baseValue={valuation.value}
            breakdown={securedBreakdown}
          />
        </div>
      </div>

      <ValuationSourceRows
        sources={sources}
        isOverride={isOverride}
        showDates={showSourceDates}
        onViewDocument={onViewDocument}
        docIdByName={docIdByName}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add Valuation Form
// ---------------------------------------------------------------------------

function AddValuationForm({
  onAdd,
  onCancel,
}: {
  onAdd: (value: number, notes: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState("");
  const [notes, setNotes] = useState("");

  return (
    <div className="space-y-3 rounded-lg border-2 border-dashed border-violet-200 p-4 dark:border-violet-800">
      <p className="text-sm font-semibold text-violet-700 dark:text-violet-400">
        Add Practitioner Valuation
      </p>
      <div>
        <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Value ($)
        </label>
        <Input
          type="number"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="0.00"
          className="h-9 text-sm tabular-nums"
        />
      </div>
      <div>
        <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Notes
        </label>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Reason for this valuation..."
          rows={2}
          className="text-sm"
        />
      </div>
      <div className="flex gap-2">
        <Button
          className="flex-1"
          disabled={!value}
          onClick={() => {
            if (value) onAdd(parseFloat(value), notes);
          }}
        >
          Add & Select
        </Button>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel Section
// ---------------------------------------------------------------------------

function PanelSection({
  title,
  count,
  action,
  children,
  collapsible,
  defaultOpen = true,
}: {
  title: string;
  count?: number;
  action?: React.ReactNode;
  children: React.ReactNode;
  /** When true, the header toggles the body open/closed. */
  collapsible?: boolean;
  /** Initial open state when collapsible (defaults to open). */
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);

  const heading = (
    <div className="flex items-center gap-2">
      <h4 className="font-sans text-xs font-semibold uppercase tracking-wider text-[#121212]">
        {title}
      </h4>
      {count != null && (
        <Badge variant="secondary" size="sm">
          {count}
        </Badge>
      )}
    </div>
  );

  if (collapsible) {
    return (
      <Collapsible open={open} onOpenChange={setOpen} className="space-y-3">
        <div className="flex items-center justify-between">
          <CollapsibleTrigger className="group flex flex-1 items-center gap-2 text-left">
            {heading}
            <ChevronDown
              className={cn(
                "h-3.5 w-3.5 text-muted-foreground transition-transform",
                !open && "-rotate-90",
              )}
            />
          </CollapsibleTrigger>
          {action}
        </div>
        <CollapsibleContent className="space-y-3">
          {children}
        </CollapsibleContent>
      </Collapsible>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        {heading}
        {action}
      </div>
      {children}
    </div>
  );
}
